import CurrencyLogo from 'components/CurrencyLogo'
import { useSortableTable } from 'hooks/analytics/useSortableTable'
import { useAllTokens } from 'hooks/Tokens'
import { memo, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import { UnifiedTokenData } from 'types/analytics'
import { formatPercentChange } from 'utils/analytics/formatPercentChange'
import { formatPriceWithMantissa } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { isMobile } from 'utils/userAgent'

import { SkeletonBar, TOKENS_TABLE_ROW_HEIGHT, TokensTableSkeletonRow } from '../Skeletons'
import TablePagination from './Pagination'
import { SortIndicator } from './SortIndicator'

interface TokensTableProps {
  tokenDatas?: UnifiedTokenData[]
  isLoading?: boolean
  maxItems?: number
  forceLoading?: boolean
  versionView?: 'v2' | 'v3'
}

enum SortField {
  name = 'name',
  priceUSD = 'priceUSD',
  priceUSDChange = 'priceUSDChange',
  priceUSDChangeWeek = 'priceUSDChangeWeek',
  volumeUSD = 'volumeUSD',
  volumeUSDWeek = 'volumeUSDWeek',
  tvlUSD = 'tvlUSD',
}

const compareNumbers =
  <T extends UnifiedTokenData>(selector: (item: T) => number | undefined) =>
  (a: T, b: T) =>
    (selector(a) ?? 0) - (selector(b) ?? 0)

const compareNames = (a: UnifiedTokenData, b: UnifiedTokenData) =>
  a.name.toLowerCase().localeCompare(b.name.toLowerCase())

const TokensTableComponent: React.FC<TokensTableProps> = ({
  tokenDatas: propTokenDatas,
  isLoading = false,
  maxItems = 20,
  forceLoading = false,
  versionView,
}: TokensTableProps) => {
  const allTokensBackend = useAllTokens()
  const tokenCatalog = useMemo(
    () =>
      Object.values(allTokensBackend).reduce<Record<string, WrappedTokenInfo>>((acc, token) => {
        acc[token.address.toLowerCase()] = token
        return acc
      }, {}),
    [allTokensBackend]
  )

  const metricsReady = useMemo(
    () =>
      Array.isArray(propTokenDatas) &&
      propTokenDatas.every((token) => token?.metrics24hLoaded ?? token?.metricsLoaded ?? false),
    [propTokenDatas]
  )

  const tokenDatas = metricsReady && propTokenDatas ? propTokenDatas : []

  const comparators = useMemo(
    () => ({
      [SortField.name]: compareNames,
      [SortField.priceUSD]: compareNumbers<UnifiedTokenData>((token) => token.priceUSD),
      [SortField.priceUSDChange]: compareNumbers<UnifiedTokenData>((token) => token.priceUSDChange),
      [SortField.priceUSDChangeWeek]: compareNumbers<UnifiedTokenData>((token) => token.priceUSDChangeWeek),
      [SortField.volumeUSD]: compareNumbers<UnifiedTokenData>((token) => token.volumeUSD),
      [SortField.volumeUSDWeek]: compareNumbers<UnifiedTokenData>((token) => token.volumeUSDWeek),
      [SortField.tvlUSD]: compareNumbers<UnifiedTokenData>((token) => token.tvlUSD),
    }),
    []
  )

  const {
    pageItems: visibleTokens,
    page,
    maxPage,
    sortField,
    sortDirection,
    handleSort,
    goToPreviousPage,
    goToNextPage,
  } = useSortableTable<UnifiedTokenData, SortField>({
    data: metricsReady ? tokenDatas : undefined,
    defaultSortField: SortField.volumeUSD,
    comparators,
    pageSize: maxItems,
  })

  const hasData = metricsReady && tokenDatas.length > 0
  const showSkeleton =
    forceLoading ||
    propTokenDatas === undefined ||
    (isLoading && !hasData) ||
    (!metricsReady && Boolean(propTokenDatas?.length))

  const linkBaseClass = 'flex h-full w-full text-inherit no-underline'
  const handleMobileNavigate = useCallback(() => {
    if (isMobile) {
      setTimeout(() => window.scrollTo({ top: 0, left: 0 }), 0)
    }
  }, [])

  if (!showSkeleton && !hasData) {
    return (
      <div className="w-full rounded-lg border border-gray-800 bg-gray-900/50 p-8">
        <div className="text-center text-gray-400">No Tokens Found</div>
      </div>
    )
  }

  return (
    <div className="w-full rounded-lg border border-gray-800 bg-gray-900/50">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-gray-400 text-xs md:text-sm">
              <th className="text-center px-2 py-3 md:p-4 font-medium w-12 md:w-16">#</th>
              <th className="text-left px-2 py-3 md:py-4 font-medium cursor-pointer">
                <button onClick={() => handleSort(SortField.name)} className="hover:text-gray-200 transition-colors">
                  Name
                  <SortIndicator field={SortField.name} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
              <th className="text-right font-medium cursor-pointer px-2 py-3 md:py-4 md:px-0 whitespace-nowrap md:w-[125px]">
                <button
                  onClick={() => handleSort(SortField.priceUSD)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  Price
                  <SortIndicator field={SortField.priceUSD} activeField={sortField} direction={sortDirection} />
                </button>
              </th>

              <th className="text-right font-medium cursor-pointer px-2 py-3 min-w-[100px] md:py-4 md:px-0 whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.priceUSDChange)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  24H
                  <SortIndicator field={SortField.priceUSDChange} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
              <th className="text-right font-medium cursor-pointer px-2 py-3 min-w-[100px] md:py-4 md:px-0 whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.priceUSDChangeWeek)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  7D
                  <SortIndicator
                    field={SortField.priceUSDChangeWeek}
                    activeField={sortField}
                    direction={sortDirection}
                  />
                </button>
              </th>

              <th className="text-right font-medium cursor-pointer px-2 py-3 md:py-4 md:px-0 whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.tvlUSD)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  TVL
                  <SortIndicator field={SortField.tvlUSD} activeField={sortField} direction={sortDirection} />
                </button>
              </th>

              <th className="text-right font-medium cursor-pointer px-2 py-3 md:py-4 md:px-0 whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.volumeUSD)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  Volume 24H
                  <SortIndicator field={SortField.volumeUSD} activeField={sortField} direction={sortDirection} />
                </button>
              </th>

              <th className="text-right pr-2  font-medium cursor-pointer px-2 py-3 md:py-4 md:px-0 whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.volumeUSDWeek)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1"
                >
                  Volume 7D
                  <SortIndicator field={SortField.volumeUSDWeek} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {showSkeleton
              ? Array.from({ length: maxItems }).map((_, index) => (
                  <TokensTableSkeletonRow key={`token-skeleton-${index}`} />
                ))
              : visibleTokens.map((token, index) => {
                  const globalIndex = (page - 1) * maxItems + index + 1
                  const metricsReady = token.metrics24hLoaded ?? false
                  const normalizedAddress = token.address.toLowerCase()
                  const tokenInfo = tokenCatalog[normalizedAddress]
                  const change24h = metricsReady ? formatPercentChange(token.priceUSDChange) : undefined
                  const change7d = metricsReady ? formatPercentChange(token.priceUSDChangeWeek) : undefined

                  const tokenLink = `/explore/${versionView}/tokens/${token.id}`
                  return (
                    <tr
                      key={token.id}
                      className="group hover:bg-gray-800/30 transition-colors cursor-pointer text-kodiak-text-primary text-sm md:text-base hover:text-kodiak-text-muted relative"
                      style={{ height: TOKENS_TABLE_ROW_HEIGHT }}
                    >
                      <td className="px-2 py-3 md:p-4 text-center w-12 md:w-16 text-sm md:text-base">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-center`}
                        >
                          {globalIndex}
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:px-4 md:py-2 md:w-[280px]">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center gap-2 md:gap-3`}
                        >
                          <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-800 bg-gray-900">
                            <CurrencyLogo currency={tokenInfo} size="28px" className="rounded-full" />
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm md:text-base font-medium text-kodiak-text-primary group-hover:text-kodiak-text-muted max-w-[110px] md:max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">
                              {token.name}
                            </span>
                            <span className="text-xs font-medium text-kodiak-text-secondary group-hover:text-kodiak-text-muted max-w-[80px] md:max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap uppercase md:normal-case tracking-wide md:tracking-normal">
                              {token.symbol}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {formatPriceWithMantissa(token.priceUSD)}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 min-w-[70px] md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div
                            className={`text-sm md:text-base font-medium tabular-nums self-start md:self-end ${
                              change24h?.className ?? 'text-kodiak-text-primary'
                            }`}
                          >
                            {metricsReady ? change24h?.text ?? '-' : <SkeletonBar width="14" className="md:ml-auto" />}
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 min-w-[70px] md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div
                            className={`text-sm md:text-base font-medium tabular-nums self-start md:self-end ${
                              change7d?.className ?? 'text-kodiak-text-primary'
                            }`}
                          >
                            {metricsReady ? change7d?.text ?? '-' : <SkeletonBar width="14" className="md:ml-auto" />}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {formatDollarAmount(token.tvlUSD)}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metricsReady ? (
                              formatDollarAmount(token.volumeUSD)
                            ) : (
                              <SkeletonBar width="20" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={tokenLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metricsReady ? (
                              formatDollarAmount(token.volumeUSDWeek)
                            ) : (
                              <SkeletonBar width="22" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>
                    </tr>
                  )
                })}
          </tbody>
        </table>
      </div>

      <TablePagination page={page} maxPage={maxPage} onPrev={goToPreviousPage} onNext={goToNextPage} />
    </div>
  )
}

const TokensTable = memo(TokensTableComponent)

TokensTable.displayName = 'TokensTable'

export default TokensTable
